import { createAsyncThunk, Unsubscribe } from "@reduxjs/toolkit";
import { Collaboration } from "src/app/models/Chat.v2";
import {
  Timestamp,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import db from "src/db";
import * as chatSlice from "../../../modules/chat/redux/ChatSlice.v2";
import Message from "src/app/models/Message";
import * as lc from "src/app/modules/localstorage/index";
import { createDocRef } from "src/app/components/list/hook/useFirestoreData";
import { Account } from "src/app/models/Account";
import { messageApiClient, ShopeeMessageHelper, LazadaMessageHelper, fetchRooms } from "../services";

// Helper to get fresh client ref (not cached at module load)
const getClientRef = () => {
  const id_client = lc.getItemLC(lc.LCName.Client)?.id;
  return createDocRef("clients", id_client);
};

// ------------- Thunk: Fetch paginated chat list
export const fetchChatList = createAsyncThunk(
  "chat/fetchChatList",
  async (
    {
      chatType,
      lastInteractionAt,
    }: { chatType: "allChat" | "myChat"; lastInteractionAt: Timestamp | null },
    { dispatch, getState }
  ) => {
    dispatch(chatSlice.setLoading({ chatType, isLoading: true }));

    try {
      const query = buildChatQuery(chatType, lastInteractionAt);
      const snap =
        (await query.get()) as unknown as QuerySnapshot<DocumentData>;

      const collaborations = await parseCollaborationSnapshot(snap);

      if (lastInteractionAt) {
        dispatch(
          chatSlice.appendCollaborations({ chatType, data: collaborations })
        );
      } else {
        dispatch(
          chatSlice.setCollaborations({ chatType, data: collaborations })
        );
      }

      dispatch(
        chatSlice.setHasMore({ chatType, hasMore: snap.docs.length === 25 })
      );
    } catch (err) {
      console.error("❌ Error fetching chat list", chatType, err);
      dispatch(chatSlice.setHasMore({ chatType, hasMore: false }));
    } finally {
      dispatch(chatSlice.setLoading({ chatType, isLoading: false }));
    }
  }
);

// ------------- Real-time subscription
export const subscribeToChatList = (
  chatType: "allChat" | "myChat",
  onUpdate: (
    data: Collaboration,
    type: "added" | "modified" | "removed"
  ) => void
): Unsubscribe => {
  const query = buildChatQuery(chatType, null);
  return onSnapshot(query, async (snapshot) => {
    const changes = snapshot.docChanges();

    for (const change of changes) {
      const doc = change.doc;
      const data = await parseSingleCollaboration(doc);
      onUpdate(data, change?.type);
    }
  });
};

// ------------- Build query by chat type
const buildChatQuery = (
  chatType: "allChat" | "myChat",
  lastInteractionAt: Timestamp | null
) => {
  const threeDaysAgo = Timestamp.fromDate(
    new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
  );

  const statusFilter = chatType === "myChat" ? "handled" : "queue";

  let query = db
    .collection("collaborations")
    .where("client", "==", getClientRef())
    .where("lastInteractionAt", ">=", threeDaysAgo)
    .where("status", "==", statusFilter)
    .orderBy("lastInteractionAt", "desc")
    .orderBy("unreadCount", "desc");

  if (lastInteractionAt) {
    query = query.startAfter(lastInteractionAt);
  }

  return query.limit(25);
};

// ------------- Parse multiple collaborations
const parseCollaborationSnapshot = async (
  snap: QuerySnapshot<DocumentData>
): Promise<Collaboration[]> => {
  const results: Collaboration[] = [];

  for (const doc of snap.docs) {
    const data = await parseSingleCollaboration(doc);
    results.push(data);
  }

  return results;
};

// ------------- Parse single collaboration
const parseSingleCollaboration = async (
  doc: QueryDocumentSnapshot<DocumentData>
): Promise<Collaboration> => {
  const data = { ...doc.data(), id: doc.id } as Collaboration;

  const rooms = await fetchRooms(doc.id);
  if (rooms.length) {
    data.roomsModel = rooms;
  }

  return data;
};


// ------------- Fetch messages for a room (initial or refresh)
export const fetchMessagesForRoom = createAsyncThunk(
  "chat/fetchMessagesForRoom",
  async (
    {
      collaborationId,
      roomId,
      conversationId,
    }: { collaborationId: string; roomId: string; conversationId: string },
    { dispatch }
  ) => {
    const key = `${collaborationId}_${roomId}`;

    dispatch(
      chatSlice.setMessagesLoading({ collaborationId, roomId, isLoading: true })
    );

    try {
      const query = buildMessagesQuery({
        collaborationId,
        roomId,
        conversationId,
      });
      const snap = await query.get();

      const messages = snap.docs.reverse().map((doc) => {
        return { ...doc.data(), id: doc.id } as Message;
      });

      dispatch(
        chatSlice.setMessagesForRoom({
          collaborationId,
          roomId,
          messages,
          hasMore: snap.docs.length === 20,
          lastVisible: snap.docs[snap.docs.length - 1],
        })
      );
    } catch (error) {
      console.error("❌ Error fetching messages", error);
      dispatch(
        chatSlice.setMessagesForRoom({
          collaborationId,
          roomId,
          messages: [],
          hasMore: false,
        })
      );
    } finally {
      dispatch(
        chatSlice.setMessagesLoading({
          collaborationId,
          roomId,
          isLoading: false,
        })
      );
    }
  }
);

// ------------- Fetch older messages (pagination)
export const fetchOlderMessagesForRoom = createAsyncThunk(
  "chat/fetchOlderMessagesForRoom",
  async (
    {
      collaborationId,
      roomId,
      conversationId,
      lastVisible,
    }: {
      collaborationId: string;
      roomId: string;
      conversationId: string;
      lastVisible: any;
    },
    { dispatch }
  ) => {
    dispatch(
      chatSlice.setMessagesLoading({ collaborationId, roomId, isLoading: true })
    );

    try {
      console.log('[THUNK-FETCH-OLDER] Building query with:', { collaborationId, roomId, conversationId, lastVisibleId: lastVisible?.id || 'null' });

      const query = buildMessagesQuery({
        collaborationId,
        roomId,
        conversationId,
        lastVisible,
      });
      const snap = await query.get();

      const messages = snap.docs.reverse().map((doc) => {
        return { ...doc.data(), id: doc.id } as Message;
      });

      dispatch(
        chatSlice.appendOlderMessages({
          collaborationId,
          roomId,
          messages,
          hasMore: snap.docs.length === 20,
          lastVisible: snap.docs[snap.docs.length - 1],
        })
      );
    } catch (error) {
      console.error("❌ Error fetching older messages", error);
    } finally {
      dispatch(
        chatSlice.setMessagesLoading({
          collaborationId,
          roomId,
          isLoading: false,
        })
      );
    }
  }
);

// ------------- Realtime listener for new messages in room
export const subscribeToMessagesForRoom = (
  collaborationId: string,
  roomId: string,
  conversationId: string,
  onChange: (message: Message, type: "added" | "modified" | "removed") => void
): Unsubscribe => {
  const query = buildMessagesQuery({ collaborationId, roomId, conversationId });
  return onSnapshot(query, (snap) => {
    snap
      .docChanges()
      .reverse()
      .forEach((change) => {
        const message = {
          ...change.doc.data(),
          id: change.doc.id,
        } as Message;

        if (
          change.type === "added" ||
          change.type === "modified" ||
          change.type === "removed"
        ) {
          onChange(message, change.type);
        }
      });
  });
};

// ------------- Build message query
const buildMessagesQuery = ({
  collaborationId,
  roomId,
  conversationId,
  lastVisible,
}: {
  collaborationId: string;
  roomId: string;
  conversationId: string;
  lastVisible?: any;
}) => {
  const roomRef = db.doc(`collaborations/${collaborationId}/rooms/${roomId}`);
  let ref = roomRef
    .collection("messages")
    .where("client", "==", getClientRef())
    .where("isActive", "==", true)
    .orderBy("createdAt", "desc")
    .limit(20);

  if (lastVisible) {
    ref = ref.startAfter(lastVisible);
  }

  return ref;
};

// ------------- Send marketplace message via unified API
/**
 * Send marketplace message via unified API
 * Supports: Shopee (now), Lazada (future), TikTok (future)
 * Server creates the message in Firestore - client just triggers the send
 */
export const sendMarketplaceMessage = createAsyncThunk(
  "chat/sendMarketplaceMessage",
  async (
    {
      platform,
      message,
      account,
      recipientId,
    }: {
      platform: "shopee" | "lazada" | "tiktok";
      message: Message;
      account: Account;
      recipientId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // Validate account has marketplaceShopId
      if (!account.marketplaceShopId) {
        throw new Error(
          `Account does not have marketplaceShopId configured for ${platform}`
        );
      }

      // Platform-specific validation and formatting
      let requestBody;
      switch (platform) {
        case "shopee":
          // Use Shopee helper for validation
          ShopeeMessageHelper.validate(message, recipientId);
          // Build Shopee request body
          requestBody = ShopeeMessageHelper.buildRequestBody(recipientId, message);
          break;

        case "lazada":
          // Use Lazada helper for validation
          LazadaMessageHelper.validate(message, recipientId);
          // Build Lazada request body
          requestBody = LazadaMessageHelper.buildRequestBody(recipientId, message);
          break;

        // Future platforms will add their helpers here
        case "tiktok":
          throw new Error("TikTok support not yet implemented");

        default:
          throw new Error(`Unsupported platform: ${platform}`);
      }

      // Send via unified API client
      const response = await messageApiClient.sendMessage(
        platform,
        account.marketplaceShopId,
        recipientId,
        message,
        requestBody
      );

      console.log(`[sendMarketplaceMessage] ${platform} success:`, response);

      // Return the server-generated message ID
      return {
        messageId: response.messages[0].id,
        status: response.messages[0].status,
        platform,
      };
    } catch (error: any) {
      console.error(`[sendMarketplaceMessage] ${platform} error:`, error);
      return rejectWithValue(error.message);
    }
  }
);
